<script setup lang="ts">
import { type StyleValue } from 'vue';
import { createPngDataUri } from 'unlazy/blurhash';

interface IProps {
    src?: string;
    hash?: string;
    borderRadius?: string | number;
    imageFit?: 'unset' | 'contain' | 'cover' | 'fill' | 'scale-down' | 'none';
    preload?: boolean;
    width?: number;
    height?: number;
    alt?: string;
    hideBlurhashOnload?: boolean;
    ratio?: string;
    blurhashConfig?: {
        ratio: number;
        size: number;
    };
    sizeUnit?: string;
    mountDelay?: boolean;
    loading?: boolean;
}

const {
    src = '',
    hash,
    borderRadius,
    imageFit = 'unset',
    width,
    height,
    alt,
    preload,
    ratio,
    hideBlurhashOnload,
    blurhashConfig,
    sizeUnit = 'px',
    loading,
} = defineProps<IProps>();

const isLoaded = ref(preload);
const pngDataUri = computed(() =>
    hash && !loading ? createPngDataUri(hash, blurhashConfig) : null
);
const blurHashImageStyles = computed<StyleValue>(() => ({
    'border-radius': borderRadius ? `${borderRadius}px` : undefined,
}));

const showBlurHashImage = computed(() => {
    if (!hash) {
        return false;
    }
    if (isLoaded.value) {
        return !hideBlurhashOnload;
    } else {
        return true;
    }
});

const ratioPercent = computed(() => {
    if (!ratio) {
        return 0;
    }
    const [x, y] = ratio.split(':');
    return (Number(y) / Number(x)) * 100;
});

const onLoad = () => {
    if (!(hash && !src)) {
        isLoaded.value = true;
    }
};
</script>

<template>
    <div
        class="blur-hash-image-container"
        :class="{ 'width-ratio': ratioPercent }"
        :style="{
            height: height ? `${height}${sizeUnit}` : '100%',
            width: width ? `${width}${sizeUnit}` : '100%',
            '--ratio': `${ratioPercent}%`,
        }"
    >
        <div v-if="loading" class="loading-skeleton blurhash loading" />
        <template v-else>
            <img
                v-if="pngDataUri && showBlurHashImage && !isLoaded && !loading"
                key="blur-hash-image"
                class="blurhash"
                :alt
                :src="pngDataUri"
                :style="blurHashImageStyles"
            />
            <mr-lazy-img
                v-if="src"
                key="original-image"
                class="image"
                :class="{ loaded: isLoaded }"
                :alt
                :src
                :preload
                :width
                :height
                :border-radius
                :image-fit
                :size-unit
                @loaded="onLoad"
            />
        </template>
    </div>
</template>

<style lang="scss" scoped>
.blur-hash-image-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    &.width-ratio {
        padding-top: var(--ratio);
        height: auto;
    }
}

.image {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    right: 0;
    z-index: 2;
    opacity: 0;
    transition: opacity 400ms;

    &.loaded {
        opacity: 1;
    }
}

.blurhash {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    right: 0;
    z-index: 1;
    &.loading {
        z-index: 4;
    }
}
</style>
